import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import api from '../services/api';
import { profileThumb, profileThumbFallback } from '../utils/imageHelpers';
import {
    Menu, X, LogOut, Settings, Home, Search,
    Music, Music2, Heart, Bell, Plus, Users, Calendar, Shield, ImageIcon, Clock, CreditCard, Disc3, ChevronDown
} from 'lucide-react';

const Navbar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isAuthenticated, logout, isArtist, isAdmin } = useAuth();
    const { unreadCount, recentNotifications, fetchRecent, markAsRead, markAllAsRead } = useNotifications();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [isNotifOpen, setIsNotifOpen] = useState(false);
    const [isMusicMenuOpen, setIsMusicMenuOpen] = useState(false);
    const [isCommunityMenuOpen, setIsCommunityMenuOpen] = useState(false);
    const [siteIcon, setSiteIcon] = useState(null);
    const [siteTitle, setSiteTitle] = useState('The Deep Wave');
    const notifRef = useRef(null);
    const profileRef = useRef(null);
    const musicMenuRef = useRef(null);
    const communityMenuRef = useRef(null);

    // Close mobile menu when location changes
    React.useEffect(() => {
        setIsMobileMenuOpen(false);
        setIsProfileMenuOpen(false);
        setIsNotifOpen(false);
        setIsMusicMenuOpen(false);
        setIsCommunityMenuOpen(false);
    }, [location.pathname]);

    // Close notification/profile/music/community dropdowns when clicking outside of them.
    // (Can't rely on a fixed-position overlay here since the navbar's backdrop-blur
    // creates a containing block that traps position:fixed elements inside it.)
    useEffect(() => {
        if (!isNotifOpen && !isProfileMenuOpen && !isMusicMenuOpen && !isCommunityMenuOpen) return;

        const handleClickOutside = (event) => {
            if (isNotifOpen && notifRef.current && !notifRef.current.contains(event.target)) {
                setIsNotifOpen(false);
            }
            if (isProfileMenuOpen && profileRef.current && !profileRef.current.contains(event.target)) {
                setIsProfileMenuOpen(false);
            }
            if (isMusicMenuOpen && musicMenuRef.current && !musicMenuRef.current.contains(event.target)) {
                setIsMusicMenuOpen(false);
            }
            if (isCommunityMenuOpen && communityMenuRef.current && !communityMenuRef.current.contains(event.target)) {
                setIsCommunityMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isNotifOpen, isProfileMenuOpen, isMusicMenuOpen, isCommunityMenuOpen]);

    // Fetch site icon, title and tagline from public endpoint
    useEffect(() => {
        const fetchSiteSettings = async () => {
            try {
                const response = await api.get('/admin/settings/public');
                if (response.data.success) {
                    const data = response.data.data;
                    if (data.site_icon) setSiteIcon(data.site_icon);
                    if (data.site_title) setSiteTitle(data.site_title);
                }
            } catch (error) {
                console.error('Error fetching site settings:', error);
            }
        };

        fetchSiteSettings();
    }, []);

    const handleLogout = async () => {
        await logout();
        navigate('/');
        setIsProfileMenuOpen(false);
        setIsMobileMenuOpen(false);
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    const closeProfileMenu = () => {
        setIsProfileMenuOpen(false);
    };

    // Navigation items for authenticated users
    const navItems = [
        { name: 'Home', href: '/dashboard', icon: Home },
        { name: 'Events', href: '/calendar', icon: Calendar },
        { name: 'Feed', href: '/feed/artist/TheDeepWave', icon: Clock },
        { name: 'Gallery', href: '/gallery/artist/TheDeepWave', icon: ImageIcon },
        { name: 'Community Feed', href: '/feed', icon: Clock },
        { name: 'Community Gallery', href: '/gallery', icon: ImageIcon },
        { name: 'Community', href: '/community', icon: Users },
        { name: 'Following', href: '/following', icon: Heart },
        ...(isArtist() ? [{ name: 'My Work', href: '/my-music', icon: Music2 }] : []),
        ...((isAdmin() || user?.id === 6 || user?.id === 7) ? [{ name: 'Admin', href: '/admin', icon: Shield }] : []),
    ];

    // Don't show navbar on auth pages
    if (location.pathname === '/login' || location.pathname === '/register') {
        return null;
    }

    return (
        <nav className="navbar-sticky glass border-b border-white/10 backdrop-blur-md">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo */}
                    <div className="flex items-center">
                        <Link 
                            to={isAuthenticated ? "/dashboard" : "/"} 
                            className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-300"
                        >
                            <div style={{ width: '60px', height: '60px', flexShrink: 0 }} className="rounded-lg overflow-hidden flex items-center justify-center">
                                {siteIcon ? (
                                    <img
                                        src={siteIcon}
                                        alt="The Deep Wave Icon"
                                        width="60"
                                        height="60"
                                        style={{ width: '60px', height: '60px' }}
                                        className="object-cover"
                                    />
                                ) : (
                                    <span className="text-3xl">🌊</span>
                                )}
                            </div>
                            <div className="flex flex-col">
                                <span className="font-black text-xl gradient-text" style={{ fontFamily: "'Shrikhand', cursive", letterSpacing: '0.05em' }}>{siteTitle}</span>
                                <span className="text-xs text-gray-400 -mt-1">South Florida Jam Band</span>
                            </div>
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-6">
                        {/* Navigation Links — identical for logged-in and logged-out users */}
                        <div className="flex items-center space-x-1.5">
                            <Link
                                to={isAuthenticated ? "/dashboard" : "/"}
                                className={`flex items-center space-x-1.5 px-2.5 py-1.5 text-sm rounded-lg transition-all duration-300 ${
                                    (isAuthenticated ? location.pathname === '/dashboard' : location.pathname === '/') ? 'nav-link-active' : 'nav-link'
                                }`}
                            >
                                <Home className="w-4 h-4" />
                                <span className="font-medium">Home</span>
                            </Link>

                            {/* Music dropdown — Music Feed + Radio */}
                            <div className="relative" ref={musicMenuRef}>
                                <button
                                    onClick={() => setIsMusicMenuOpen(o => !o)}
                                    className={`flex items-center space-x-1.5 px-2.5 py-1.5 text-sm rounded-lg transition-all duration-300 ${
                                        (location.pathname.startsWith('/feed/artist/TheDeepWave/audio') || location.pathname === '/radio')
                                            ? 'nav-link-active'
                                            : 'nav-link'
                                    }`}
                                >
                                    <Music className="w-4 h-4" />
                                    <span className="font-medium">Music</span>
                                    <ChevronDown className={`w-3 h-3 transition-transform ${isMusicMenuOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {isMusicMenuOpen && (
                                    <div className="absolute left-0 mt-2 w-48 bg-gray-900 rounded-xl border border-white/10 shadow-2xl py-2 z-50">
                                        <Link
                                            to="/feed/artist/TheDeepWave/audio"
                                            onClick={() => setIsMusicMenuOpen(false)}
                                            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                                        >
                                            <Music className="w-4 h-4" />
                                            <span>Music Feed</span>
                                        </Link>
                                        <Link
                                            to="/radio"
                                            onClick={() => setIsMusicMenuOpen(false)}
                                            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                                        >
                                            <Disc3 className="w-4 h-4" />
                                            <span>Radio</span>
                                        </Link>
                                    </div>
                                )}
                            </div>

                            {navItems.filter(item => item.name !== 'Home' && item.name !== 'Admin' && item.name !== 'Community Feed' && item.name !== 'Community Gallery' && (isAuthenticated || item.name !== 'Following')).map((item) => {
                                const IconComponent = item.icon;

                                // Community dropdown — Community + Community Feed + Community Gallery
                                if (item.name === 'Community') {
                                    const isCommunityActive = location.pathname === '/community' || location.pathname === '/feed' || location.pathname === '/gallery';
                                    return (
                                        <div className="relative" ref={communityMenuRef} key={item.name}>
                                            <button
                                                onClick={() => setIsCommunityMenuOpen(o => !o)}
                                                className={`flex items-center space-x-1.5 px-2.5 py-1.5 text-sm rounded-lg transition-all duration-300 ${
                                                    isCommunityActive ? 'nav-link-active' : 'nav-link'
                                                }`}
                                            >
                                                <IconComponent className="w-4 h-4" />
                                                <span className="font-medium">Community</span>
                                                <ChevronDown className={`w-3 h-3 transition-transform ${isCommunityMenuOpen ? 'rotate-180' : ''}`} />
                                            </button>
                                            {isCommunityMenuOpen && (
                                                <div className="absolute left-0 mt-2 w-56 bg-gray-900 rounded-xl border border-white/10 shadow-2xl py-2 z-50">
                                                    <Link
                                                        to="/feed"
                                                        onClick={() => setIsCommunityMenuOpen(false)}
                                                        className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                                                    >
                                                        <Clock className="w-4 h-4" />
                                                        <span>Community Feed</span>
                                                    </Link>
                                                    <Link
                                                        to="/gallery"
                                                        onClick={() => setIsCommunityMenuOpen(false)}
                                                        className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                                                    >
                                                        <ImageIcon className="w-4 h-4" />
                                                        <span>Community Gallery</span>
                                                    </Link>
                                                    <Link
                                                        to="/community"
                                                        onClick={() => setIsCommunityMenuOpen(false)}
                                                        className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                                                    >
                                                        <Users className="w-4 h-4" />
                                                        <span>Community</span>
                                                    </Link>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }

                                const isActive = location.pathname === item.href;
                                return (
                                    <Link
                                        key={item.name}
                                        to={item.href}
                                        className={`flex items-center space-x-1.5 px-2.5 py-1.5 text-sm rounded-lg transition-all duration-300 ${
                                            isActive
                                                ? 'nav-link-active'
                                                : 'nav-link'
                                        }`}
                                    >
                                        <IconComponent className="w-4 h-4" />
                                        <span className="font-medium">{item.name}</span>
                                    </Link>
                                );
                            })}
                        </div>

                        {/* Right side — the only part that differs between logged-in and logged-out */}
                        {isAuthenticated ? (
                            <div className="flex items-center space-x-4">
                                {/* Notifications */}
                                <div className="relative" ref={notifRef}>
                                    <button
                                        aria-label="Notifications"
                                        onClick={() => {
                                            const opening = !isNotifOpen;
                                            setIsNotifOpen(opening);
                                            if (opening) fetchRecent();
                                        }}
                                        className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/5 transition-all duration-300 relative"
                                    >
                                        <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-green-400' : ''}`} />
                                        {unreadCount > 0 && (
                                            <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full"></span>
                                        )}
                                    </button>

                                    {isNotifOpen && (
                                        <div className="absolute right-0 mt-2 w-80 bg-gray-900 rounded-xl border border-white/10 shadow-2xl max-h-96 overflow-y-auto">
                                            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                                                <p className="text-sm font-semibold text-white">Notifications</p>
                                                {unreadCount > 0 && (
                                                    <button
                                                        onClick={markAllAsRead}
                                                        className="text-xs text-blue-400 hover:text-blue-300"
                                                    >
                                                        Mark all read
                                                    </button>
                                                )}
                                            </div>
                                            {recentNotifications.length === 0 ? (
                                                <p className="px-4 py-6 text-sm text-gray-500 text-center">No notifications yet</p>
                                            ) : (
                                                recentNotifications.map((n) => (
                                                    <button
                                                        key={n.id}
                                                        onClick={() => {
                                                            markAsRead(n.id);
                                                            setIsNotifOpen(false);
                                                            if (n.link) navigate(n.link);
                                                        }}
                                                        className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${!n.is_read ? 'bg-green-500/5' : ''}`}
                                                    >
                                                        <p className="text-sm text-white">{n.title}</p>
                                                        <p className="text-xs text-gray-400 line-clamp-2">{n.message}</p>
                                                    </button>
                                                ))
                                            )}
                                            <button
                                                onClick={() => {
                                                    setIsNotifOpen(false);
                                                    navigate('/notifications');
                                                }}
                                                className="w-full text-center py-3 text-sm text-blue-400 hover:text-blue-300 border-t border-white/10"
                                            >
                                                View All
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Create Post */}
                                {isArtist() && (
                                    <Link
                                        to="/create"
                                        className="btn-primary py-2 px-4 text-sm flex items-center gap-2"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Create
                                    </Link>
                                )}

                                {/* Profile Menu */}
                                <div className="relative" ref={profileRef}>
                                    <button
                                        onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                                        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-white/5 transition-all duration-300"
                                    >
                                        <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center">
                                            {user?.profile?.profilePictureUrl ? (
                                                <img
                                                    src={profileThumb(user.profile.profilePictureUrl)}
                                                    alt="Profile"
                                                    width="32"
                                                    height="32"
                                                    className="w-8 h-8 rounded-full object-cover"
                                                    style={{ background: 'transparent' }}
                                                    onError={profileThumbFallback(user.profile.profilePictureUrl)}
                                                />
                                            ) : (
                                                <span className="text-sm leading-none">🌊</span>
                                            )}
                                        </div>
                                        <span className="text-sm font-medium text-gray-300 hidden lg:block">
                                            {user?.profile?.displayName || 'User'}
                                        </span>
                                    </button>

                                    {/* Profile Dropdown */}
                                    {isProfileMenuOpen && (
                                        <div className="absolute right-0 mt-2 w-56 bg-gray-900 rounded-xl border border-white/10 shadow-2xl">
                                            <div className="px-4 py-3 border-b border-white/10">
                                                <p className="text-sm font-semibold text-white">
                                                    {user?.profile?.displayName || 'User'}
                                                </p>
                                                <p className="text-xs text-gray-400">
                                                    @{user?.profile?.username}
                                                    {isArtist() && <span className="text-blue-400"> · Creator</span>}
                                                </p>
                                            </div>
                                            <div className="py-2">
                                                <Link
                                                    to={`/profile/${user?.profile?.username}`}
                                                    onClick={closeProfileMenu}
                                                    className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors duration-300"
                                                >
                                                    <span>🌊</span>
                                                    <span>View Profile</span>
                                                </Link>
                                                <Link
                                                    to="/settings"
                                                    onClick={closeProfileMenu}
                                                    className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors duration-300"
                                                >
                                                    <Settings className="w-4 h-4" />
                                                    <span>Settings</span>
                                                </Link>
                                                <Link
                                                    to="/account"
                                                    onClick={closeProfileMenu}
                                                    className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors duration-300"
                                                >
                                                    <CreditCard className="w-4 h-4" />
                                                    <span>Account</span>
                                                </Link>
                                                {(isAdmin() || user?.id === 6 || user?.id === 7) && (
                                                    <Link
                                                        to="/admin"
                                                        onClick={closeProfileMenu}
                                                        className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors duration-300"
                                                    >
                                                        <Shield className="w-4 h-4" />
                                                        <span>Admin</span>
                                                    </Link>
                                                )}
                                                <button
                                                    onClick={handleLogout}
                                                    className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-colors duration-300"
                                                >
                                                    <LogOut className="w-4 h-4" />
                                                    <span>Sign Out</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center space-x-2">
                                <Link to="/login" className="btn-ghost !px-3 !py-1.5 text-sm">
                                    Login
                                </Link>
                                <Link to="/register" className="btn-primary !px-4 !py-2 text-sm">
                                    Sign Up
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Mobile menu button */}
                    <div className="md:hidden">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="p-3 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-all duration-300 touch-manipulation min-h-[48px] min-w-[48px] flex items-center justify-center mr-2"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                        >
                            {isMobileMenuOpen ? (
                                <X className="w-6 h-6" />
                            ) : (
                                <Menu className="w-6 h-6" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation Menu */}
            {isMobileMenuOpen && (
                <div className="md:hidden border-t border-white/10 bg-gray-900/95 backdrop-blur-lg relative z-50 animate-fade-in">
                    <div className="px-4 py-3 space-y-2 max-h-[calc(100vh-4rem)] overflow-y-auto">
                        {isAuthenticated ? (
                            <>
                                {/* User Info */}
                                <div className="flex items-center space-x-3 pb-3 border-b border-white/10">
                                    <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center">
                                        {user?.profile?.profilePictureUrl ? (
                                            <img
                                                src={profileThumb(user.profile.profilePictureUrl)}
                                                alt="Profile"
                                                width="40"
                                                height="40"
                                                className="w-10 h-10 rounded-full object-cover"
                                                style={{ background: 'transparent' }}
                                                onError={profileThumbFallback(user.profile.profilePictureUrl)}
                                            />
                                        ) : (
                                            <span className="text-base leading-none">🌊</span>
                                        )}
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-white">
                                            {user?.profile?.displayName || 'User'}
                                        </p>
                                        <p className="text-xs text-gray-400">
                                            @{user?.profile?.username}
                                            {isArtist() && <span className="text-blue-400"> · Creator</span>}
                                        </p>
                                    </div>
                                </div>

                                {/* Navigation Links */}
                                <div className="space-y-1">
                                    <Link
                                        to="/dashboard"
                                        onClick={closeMobileMenu}
                                        className={`flex items-center space-x-3 px-4 py-2 text-base rounded-lg transition-all duration-300 touch-manipulation min-h-[40px] ${
                                            location.pathname === '/dashboard' ? 'nav-link-active' : 'nav-link'
                                        }`}
                                        style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                        <Home className="w-5 h-5" />
                                        <span className="font-medium">Home</span>
                                    </Link>
                                    <Link
                                        to="/feed/artist/TheDeepWave/audio"
                                        onClick={closeMobileMenu}
                                        className={`flex items-center space-x-3 px-4 py-2 text-base rounded-lg transition-all duration-300 touch-manipulation min-h-[40px] ${
                                            location.pathname.startsWith('/feed/artist/TheDeepWave/audio') ? 'nav-link-active' : 'nav-link'
                                        }`}
                                        style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                        <Music className="w-5 h-5" />
                                        <span className="font-medium">Music Feed</span>
                                    </Link>
                                    <Link
                                        to="/radio"
                                        onClick={closeMobileMenu}
                                        className={`flex items-center space-x-3 px-4 py-2 text-base rounded-lg transition-all duration-300 touch-manipulation min-h-[40px] ${
                                            location.pathname === '/radio' ? 'nav-link-active' : 'nav-link'
                                        }`}
                                        style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                        <Disc3 className="w-5 h-5" />
                                        <span className="font-medium">Radio</span>
                                    </Link>
                                    {navItems.filter(item => item.name !== 'Home').map((item) => {
                                        const IconComponent = item.icon;
                                        const isActive = location.pathname === item.href;
                                        return (
                                            <Link
                                                key={item.name}
                                                to={item.href}
                                                onClick={closeMobileMenu}
                                                className={`flex items-center space-x-3 px-4 py-2 text-base rounded-lg transition-all duration-300 touch-manipulation min-h-[40px] ${
                                                    isActive
                                                        ? 'nav-link-active'
                                                        : 'nav-link'
                                                }`}
                                                style={{ WebkitTapHighlightColor: 'transparent' }}
                                            >
                                                <IconComponent className="w-5 h-5" />
                                                <span className="font-medium">{item.name}</span>
                                            </Link>
                                        );
                                    })}
                                </div>

                                {/* Action Items */}
                                <div className="pt-3 border-t border-white/10 space-y-1">
                                    <Link
                                        to={`/profile/${user?.profile?.username}`}
                                        onClick={closeMobileMenu}
                                        className="flex items-center space-x-3 px-4 py-2 text-base nav-link rounded-lg touch-manipulation min-h-[40px]"
                                        style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                        <span>🌊</span>
                                        <span>View Profile</span>
                                    </Link>
                                    <Link
                                        to="/notifications"
                                        onClick={closeMobileMenu}
                                        className="flex items-center space-x-3 px-4 py-2 text-base nav-link rounded-lg touch-manipulation min-h-[40px]"
                                        style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                        <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-green-400' : ''}`} />
                                        <span>Notifications</span>
                                        {unreadCount > 0 && (
                                            <span className="ml-auto w-2 h-2 bg-green-500 rounded-full"></span>
                                        )}
                                    </Link>
                                    <Link
                                        to="/settings"
                                        onClick={closeMobileMenu}
                                        className="flex items-center space-x-3 px-4 py-2 text-base nav-link rounded-lg touch-manipulation min-h-[40px]"
                                        style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                        <Settings className="w-5 h-5" />
                                        <span>Settings</span>
                                    </Link>
                                    <Link
                                        to="/account"
                                        onClick={closeMobileMenu}
                                        className="flex items-center space-x-3 px-4 py-2 text-base nav-link rounded-lg touch-manipulation min-h-[40px]"
                                        style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                        <CreditCard className="w-5 h-5" />
                                        <span>Account</span>
                                    </Link>
                                    <button
                                        onClick={handleLogout}
                                        className="w-full flex items-center space-x-3 px-4 py-2 text-base nav-link rounded-lg text-left touch-manipulation min-h-[40px]"
                                        style={{ WebkitTapHighlightColor: 'transparent' }}
                                    >
                                        <LogOut className="w-5 h-5" />
                                        <span>Sign Out</span>
                                    </button>
                                </div>
                            </>
                        ) : (
                            /* Guest Mobile Menu */
                            <div className="space-y-1">
                                <Link
                                    to="/"
                                    onClick={closeMobileMenu}
                                    className="flex items-center space-x-3 px-3 py-2 text-base nav-link rounded-lg"
                                >
                                    <Home className="w-5 h-5" />
                                    <span>Home</span>
                                </Link>
                                <Link
                                    to="/feed/artist/TheDeepWave/audio"
                                    onClick={closeMobileMenu}
                                    className="flex items-center space-x-3 px-3 py-2 text-base nav-link rounded-lg"
                                >
                                    <Music className="w-5 h-5" />
                                    <span>Music Feed</span>
                                </Link>
                                <Link
                                    to="/radio"
                                    onClick={closeMobileMenu}
                                    className="flex items-center space-x-3 px-3 py-2 text-base nav-link rounded-lg"
                                >
                                    <Disc3 className="w-5 h-5" />
                                    <span>Radio</span>
                                </Link>
                                <Link
                                    to="/calendar"
                                    onClick={closeMobileMenu}
                                    className="flex items-center space-x-3 px-3 py-2 text-base nav-link rounded-lg"
                                >
                                    <Calendar className="w-5 h-5" />
                                    <span>Events</span>
                                </Link>
                                <Link
                                    to="/feed/artist/TheDeepWave"
                                    onClick={closeMobileMenu}
                                    className="flex items-center space-x-3 px-3 py-2 text-base nav-link rounded-lg"
                                >
                                    <Clock className="w-5 h-5" />
                                    <span>Feed</span>
                                </Link>
                                <Link
                                    to="/gallery/artist/TheDeepWave"
                                    onClick={closeMobileMenu}
                                    className="flex items-center space-x-3 px-3 py-2 text-base nav-link rounded-lg"
                                >
                                    <ImageIcon className="w-5 h-5" />
                                    <span>Gallery</span>
                                </Link>
                                <Link
                                    to="/feed"
                                    onClick={closeMobileMenu}
                                    className="flex items-center space-x-3 px-3 py-2 text-base nav-link rounded-lg"
                                >
                                    <Clock className="w-5 h-5" />
                                    <span>Community Feed</span>
                                </Link>
                                <Link
                                    to="/gallery"
                                    onClick={closeMobileMenu}
                                    className="flex items-center space-x-3 px-3 py-2 text-base nav-link rounded-lg"
                                >
                                    <ImageIcon className="w-5 h-5" />
                                    <span>Community Gallery</span>
                                </Link>
                                <Link
                                    to="/community"
                                    onClick={closeMobileMenu}
                                    className="flex items-center space-x-3 px-3 py-2 text-base nav-link rounded-lg"
                                >
                                    <Users className="w-5 h-5" />
                                    <span>Community</span>
                                </Link>
                                <Link
                                    to="/login"
                                    onClick={closeMobileMenu}
                                    className="block w-full btn-ghost text-center"
                                >
                                    Login
                                </Link>
                                <Link
                                    to="/register"
                                    onClick={closeMobileMenu}
                                    className="block w-full btn-primary text-center"
                                >
                                    Sign Up
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            )}

        </nav>
    );
};

export default Navbar;